import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPackage, FiPieChart } = FiIcons;

const StrengthAnalysis = ({ selectedProduct }) => {
  // Mock strength share data
  const strengthData = [
    { strength: '250mg', share: 68.2, color: '#3b82f6' },
    { strength: '500mg', share: 31.8, color: '#10b981' }
  ];

  // Mock pack share data
  const packData = [
    { pack: '30 count', share: 45.3, color: '#3b82f6' },
    { pack: '60 count', share: 32.7, color: '#10b981' },
    { pack: '90 count', share: 22.0, color: '#f59e0b' }
  ];

  // Mock detailed market share table
  const marketShareData = [
    {
      manufacturer: 'NOVADOZ PHARMA',
      jan: 42.36, feb: 43.28, mar: 44.1, apr: 43.86, may: 43.48, jun: 44.35,
      jul: 45.46, aug: 45.76, sep: 46.4, oct: 47.16, nov: 46.57, dec: 46.89,
      total: 45.0
    },
    {
      manufacturer: 'NORTHSTAR RX',
      jan: 18.50, feb: 17.95, mar: 17.32, apr: 16.88, may: 17.53, jun: 16.85,
      jul: 16.46, aug: 16.13, sep: 15.87, oct: 15.43, nov: 15.86, dec: 15.24,
      total: 16.5
    },
    {
      manufacturer: 'APOTEX CORP',
      jan: 14.27, feb: 14.18, mar: 14.09, apr: 14.33, may: 14.72, jun: 14.31,
      jul: 13.87, aug: 13.56, sep: 13.24, oct: 12.95, nov: 13.42, dec: 13.78,
      total: 13.9
    },
    {
      manufacturer: 'NOVAGEN PHARMA',
      jan: 8.24, feb: 8.96, mar: 9.18, apr: 9.63, may: 8.79, jun: 9.09,
      jul: 8.84, aug: 8.97, sep: 9.11, oct: 8.76, nov: 8.95, dec: 9.17,
      total: 8.9
    }
  ];

  // Mock MAT data
  const matData = [
    { strength: '250mg', pack: '30 count', eaches: 22677, nspEaches: 15118, novadozShare: 45.6, nspEst: 30236, sales: 2106538 },
    { strength: '250mg', pack: '60 count', eaches: 305295, nspEaches: 203530, novadozShare: 67.9, nspEst: 610590, sales: 8532745 },
    { strength: '500mg', pack: '30 count', eaches: 30059, nspEaches: 20039, novadozShare: 21.4, nspEst: 60118, sales: 1694180 },
    { strength: '500mg', pack: '60 count', eaches: 3546, nspEaches: 2364, novadozShare: 71.2, nspEst: 7092, sales: 198618 }
  ];

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Pie Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strength Share */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiPieChart} className="text-blue-600" />
            Strength Share (Last 12 Months Total)
          </h3>
          
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                {strengthData.reduce((acc, item, index) => {
                  const startAngle = acc.currentAngle;
                  const endAngle = startAngle + (item.share / 100) * 360;
                  const largeArcFlag = item.share > 50 ? 1 : 0;
                  
                  const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                  const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                  
                  const pathData = [
                    `M 50 50`,
                    `L ${x1} ${y1}`,
                    `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                    `Z`
                  ].join(' ');
                  
                  acc.paths.push(
                    <path
                      key={index}
                      d={pathData}
                      fill={item.color}
                      className="hover:opacity-80 transition-opacity"
                    />
                  );
                  
                  acc.currentAngle = endAngle;
                  return acc;
                }, { paths: [], currentAngle: 0 }).paths}
              </svg>
            </div>
          </div>
          
          <div className="space-y-3">
            {strengthData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="font-medium text-gray-900">{item.strength}</span>
                </div>
                <span className="font-semibold text-gray-900">{item.share}%</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pack Share */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiPackage} className="text-green-600" />
            Pack Share (Last 12 Months Total)
          </h3>
          
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                {packData.reduce((acc, item, index) => {
                  const startAngle = acc.currentAngle;
                  const endAngle = startAngle + (item.share / 100) * 360;
                  const largeArcFlag = item.share > 50 ? 1 : 0;
                  
                  const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                  const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                  
                  const pathData = [
                    `M 50 50`,
                    `L ${x1} ${y1}`,
                    `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                    `Z`
                  ].join(' ');
                  
                  acc.paths.push(
                    <path
                      key={index}
                      d={pathData}
                      fill={item.color}
                      className="hover:opacity-80 transition-opacity"
                    />
                  );
                  
                  acc.currentAngle = endAngle;
                  return acc;
                }, { paths: [], currentAngle: 0 }).paths}
              </svg>
            </div>
          </div>
          
          <div className="space-y-3">
            {packData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="font-medium text-gray-900">{item.pack}</span>
                </div>
                <span className="font-semibold text-gray-900">{item.share}%</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Market Share Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Market Share % (Last 12 Months) - {selectedProduct}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Manufacturer</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Jan</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Feb</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Mar</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Apr</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">May</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Jun</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Jul</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Aug</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Sep</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Oct</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Nov</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Dec</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Grand Total</th>
              </tr>
            </thead>
            <tbody>
              {marketShareData.map((row, index) => (
                <tr key={index} className={`border-b border-gray-100 ${index === 0 ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <td className={`py-3 px-4 font-medium ${index === 0 ? 'text-blue-900' : 'text-gray-900'}`}>
                    {row.manufacturer}
                  </td>
                  <td className="py-3 px-4 text-center text-sm">{row.jan}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.feb}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.mar}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.apr}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.may}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.jun}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.jul}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.aug}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.sep}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.oct}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.nov}%</td>
                  <td className="py-3 px-4 text-center text-sm">{row.dec}%</td>
                  <td className={`py-3 px-4 text-center font-bold ${index === 0 ? 'text-blue-900' : 'text-gray-900'}`}>
                    {row.total}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* MAT Value and Volume Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            MAT Value and Volume - {selectedProduct}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Strength</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Pack</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">Eaches</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">% (Eaches)</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">Units</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">NSP Ext Units</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">Novadoz Share %</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">Sales $</th>
              </tr>
            </thead>
            <tbody>
              {matData.map((row, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{row.strength}</td>
                  <td className="py-3 px-4 text-gray-700">{row.pack}</td>
                  <td className="py-3 px-4 text-right">{formatNumber(row.eaches)}</td>
                  <td className="py-3 px-4 text-right text-gray-700">{((row.eaches / 361577) * 100).toFixed(1)}%</td>
                  <td className="py-3 px-4 text-right">{formatNumber(row.nspEaches)}</td>
                  <td className="py-3 px-4 text-right">{formatNumber(row.nspEst)}</td>
                  <td className="py-3 px-4 text-right font-medium text-blue-600">{row.novadozShare}%</td>
                  <td className="py-3 px-4 text-right font-medium">{formatCurrency(row.sales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default StrengthAnalysis;